import { difference } from 'lodash';
import { useCallback, useMemo } from 'react';
import { splitArray } from 'utils/arrayHelpers';
import { PRIORITIZED_COLUMNS, STARRED_FIELDS } from '../constants';
import { UserField } from '../types';
import { useIsUuidProject } from './useIsUuidProject';

/**
 *
 * @param prioritizedFields
 * @returns List of user fields with prioritzed fields at the start
 */
export const usePrioritizeUserFields = (userFields: UserField[]): UserField[] => {
  // Extract starred fields if included
  const isUuidBased = useIsUuidProject();

  const sortStarred = useCallback(
    (a: string) => {
      if (isUuidBased) {
        return a === STARRED_FIELDS.userId ? -1 : 1;
      }
      return a === STARRED_FIELDS.email ? -1 : 1;
    },
    [isUuidBased],
  );

  return useMemo(() => {
    const [matchedArray, unmatchedArray] = splitArray(
      [...PRIORITIZED_COLUMNS],
      (item) => item in STARRED_FIELDS,
    );

    // Add sorted starred fields back at the start of the prioritized list
    const prioritizedWithStarredValues = [...matchedArray.sort(sortStarred), ...unmatchedArray];

    // Reorder user fields to prioritize selected fields
    const reorderedFields = [...prioritizedWithStarredValues].reduce((acc, value) => {
      const matchingFields = userFields.filter(
        (userField) => userField.field.toLowerCase() === value.toLowerCase(),
      );
      return [...acc, ...matchingFields];
    }, [] as UserField[]);

    const remainingFields = difference(userFields, reorderedFields);
    return [...reorderedFields, ...remainingFields];
  }, [userFields]);
};
